/**
 * EV tunable 配置种子值（统一在前端维护，后端不再内置默认）。
 */

export function createSeedTunableConfig() {
  return {
    wLoc: { 1: 1.1, 2: 1.0, 3: 0.1, 4: 1.0 },
    rPtsByArea: { 1: 35, 2: 30, 3: 25, 4: 25 },
    wTimeByHour: {},
    wTimeByAreaHour: { 1: { 17: 0.2, 18: 2.0, 19: 2.0 } },
    roiBreakpoints: [
      { minutes: 7 * 24 * 60, value: 1.2 },
      { minutes: 3 * 24 * 60, value: 0.9 },
      { minutes: 1 * 24 * 60, value: 0.6 },
      { minutes: 4 * 60, value: 0.3 }
    ],
    gammaBreakpoints: [
      { minutes: 2 * 24 * 60, value: 0.2 },
      { minutes: 4 * 60, value: 0.75 },
      { minutes: 0, value: 0.95 }
    ],
    betaBreakpoints: [
      { minutes: 1 * 24 * 60, value: 1.0 },
      { minutes: 2 * 60, value: 0.1 },
      { minutes: 0, value: 0.0 }
    ],
    muFloor: 0.4,
    muCeil: 2.0,
    muNormalCars: 2,
    muHotCars: 6,
    muSurplusBufferK: 2,
    muSurplusBufferKByArea: {},
    muSurplusBinCapK: 8,
    muSurplusLambda: 0.4,
    muSurplusFloor: 0,
    unusedCarCost: 1.0,
    sellableSeatR0: 400,
    tripCostByRoute: {},
    wDateWeekday: 1.0,
    wDateWeekend: 1.1,
    wDateHoliday: 1.3,
    wDateByDate: {}
  };
}

export function hasMapKeys(m) {
  return m && Object.keys(m).length > 0;
}

export function hasNestedMapKeys(m) {
  if (!m) return false;
  return Object.keys(m).some((k) => m[k] && Object.keys(m[k]).length > 0);
}

export function hasBreakpoints(arr) {
  return arr && arr.length > 0;
}

export function isTunableConfigEmpty(data) {
  if (!data) return true;
  return !hasMapKeys(data.wLoc)
    && !hasMapKeys(data.rPtsByArea)
    && !hasMapKeys(data.wTimeByHour)
    && !hasNestedMapKeys(data.wTimeByAreaHour)
    && !hasBreakpoints(data.roiBreakpoints)
    && !hasBreakpoints(data.gammaBreakpoints)
    && !hasBreakpoints(data.betaBreakpoints);
}

/** 服务端 map 未持久化时，用种子补全（保留已有断点等字段） */
export function mergeTunableWithSeed(data) {
  const seed = createSeedTunableConfig();
  const src = data || {};
  return {
    wLoc: hasMapKeys(src.wLoc) ? copyNumberKeyMap(src.wLoc) : { ...seed.wLoc },
    rPtsByArea: hasMapKeys(src.rPtsByArea) ? copyNumberKeyMap(src.rPtsByArea) : { ...seed.rPtsByArea },
    wTimeByHour: hasMapKeys(src.wTimeByHour) ? copyNumberKeyMap(src.wTimeByHour) : { ...seed.wTimeByHour },
    wTimeByAreaHour: hasNestedMapKeys(src.wTimeByAreaHour)
      ? copyNestedNumberKeyMap(src.wTimeByAreaHour)
      : copyNestedNumberKeyMap(seed.wTimeByAreaHour),
    roiBreakpoints: hasBreakpoints(src.roiBreakpoints)
      ? copyBreakpoints(src.roiBreakpoints)
      : [...seed.roiBreakpoints],
    gammaBreakpoints: hasBreakpoints(src.gammaBreakpoints)
      ? copyBreakpoints(src.gammaBreakpoints)
      : [...seed.gammaBreakpoints],
    betaBreakpoints: hasBreakpoints(src.betaBreakpoints)
      ? copyBreakpoints(src.betaBreakpoints)
      : [...seed.betaBreakpoints],
    muFloor: src.muFloor != null ? src.muFloor : seed.muFloor,
    muCeil: src.muCeil != null ? src.muCeil : seed.muCeil,
    muNormalCars: src.muNormalCars != null ? src.muNormalCars : seed.muNormalCars,
    muHotCars: src.muHotCars != null ? src.muHotCars : seed.muHotCars,
    muSurplusBufferK: src.muSurplusBufferK != null ? src.muSurplusBufferK : seed.muSurplusBufferK,
    muSurplusBufferKByArea: src.muSurplusBufferKByArea != null
      ? copyNumberKeyMap(src.muSurplusBufferKByArea)
      : { ...seed.muSurplusBufferKByArea },
    muSurplusBinCapK: src.muSurplusBinCapK != null ? src.muSurplusBinCapK : seed.muSurplusBinCapK,
    muSurplusLambda: src.muSurplusLambda != null ? src.muSurplusLambda : seed.muSurplusLambda,
    muSurplusFloor: src.muSurplusFloor != null ? src.muSurplusFloor : seed.muSurplusFloor,
    unusedCarCost: src.unusedCarCost != null ? src.unusedCarCost : seed.unusedCarCost,
    sellableSeatR0: src.sellableSeatR0 != null ? src.sellableSeatR0 : seed.sellableSeatR0,
    tripCostByRoute: hasMapKeys(src.tripCostByRoute) ? copyNumberKeyMap(src.tripCostByRoute) : { ...seed.tripCostByRoute },
    wDateWeekday: src.wDateWeekday != null ? src.wDateWeekday : seed.wDateWeekday,
    wDateWeekend: src.wDateWeekend != null ? src.wDateWeekend : seed.wDateWeekend,
    wDateHoliday: src.wDateHoliday != null ? src.wDateHoliday : seed.wDateHoliday,
    wDateByDate: hasMapKeys(src.wDateByDate) ? copyStringKeyMap(src.wDateByDate) : { ...seed.wDateByDate }
  };
}

/** 服务端是否存在未持久化的 map 字段（常见于仅保存了断点） */
export function hasUnpersistedMaps(data) {
  if (!data) return true;
  return !hasMapKeys(data.wLoc)
    || !hasMapKeys(data.rPtsByArea)
    || (!hasMapKeys(data.wTimeByHour) && !hasNestedMapKeys(data.wTimeByAreaHour));
}

function copyNumberKeyMap(map) {
  const result = {};
  Object.keys(map || {}).forEach((key) => {
    const n = Number(key);
    if (!Number.isNaN(n) && map[key] != null) {
      result[n] = map[key];
    }
  });
  return result;
}

function copyStringKeyMap(map) {
  const result = {};
  Object.keys(map || {}).forEach((key) => {
    if (key && map[key] != null) {
      result[key] = map[key];
    }
  });
  return result;
}

function copyNestedNumberKeyMap(map) {
  const result = {};
  Object.keys(map || {}).forEach((areaKey) => {
    const areaId = Number(areaKey);
    if (!Number.isNaN(areaId)) {
      result[areaId] = copyNumberKeyMap(map[areaKey]);
    }
  });
  return result;
}

function copyBreakpoints(list) {
  return (list || []).map((bp) => ({ minutes: bp.minutes, value: bp.value }));
}

export function resolveTunableConfig(data) {
  return mergeTunableWithSeed(data);
}
